import { useMutation, useQuery } from "@apollo/client";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import IllustrationsPageIllustrationDetails from "../components/IllustrationsPageIllustrationDetails";
import IllustrationsPageCreateCont from "../containers/IllustrationsPageCreateCont";
import { DELETE_ILLUSTRATION } from "../gql/mutations/illustration";
import { QUERY_ILLUSTRATIONS } from "../gql/queries/illustration";

import { DEFAULT_OFFSET, ROWS_PER_PAGE_DEFAULT, ROWS_PER_PAGE_OPTIONS } from "../helpers/const";
import { useOnOffSwitch, usePagination } from "../helpers/hooks";
import { Spacing } from "../types/enum";
import { type IllustrationModel } from "../types/graphql";

const IllustrationsPage: React.FC = () => {
  const [open, onOpen, onClose] = useOnOffSwitch();
  const [offset, setOffset] = useState<number>(DEFAULT_OFFSET);
  const [limit, setLimit] = useState<number>(ROWS_PER_PAGE_DEFAULT);

  const { data, loading } = useQuery(QUERY_ILLUSTRATIONS, {
    fetchPolicy: "network-only",
    variables: {
      filters: {
        limit,
        offset,
      },
    },
  });

  const { page, rowsPerPage, items, onChangePage, onChangeRowsPerPage } =
    usePagination<IllustrationModel>(data?.adminListPaginatedIllustrations?.items);

  useEffect(() => {
    setOffset(page * rowsPerPage);
  }, [page, rowsPerPage]);

  useEffect(() => {
    setLimit(rowsPerPage);
  }, [rowsPerPage]);

  const [deleteIllustration] = useMutation(DELETE_ILLUSTRATION, {
    refetchQueries: [
      {
        query: QUERY_ILLUSTRATIONS,
        variables: {
          filters: {
            limit,
            offset,
          },
        },
      },
    ],
  });

  const { enqueueSnackbar } = useSnackbar();

  const onDelete = async (illustrationId: string) => {
    const variables = {
      id: illustrationId,
    };

    try {
      await deleteIllustration({ variables });
    } catch (error: any) {
      enqueueSnackbar(error.message, { variant: "error" });
    }
  };

  return (
    <>
      <Box mb={Spacing.m} display="flex" alignItems="center" justifyContent="space-between">
        <Typography variant="h3" color="textPrimary">
          All Illustrations
        </Typography>

        <Button variant="contained" color="secondary" onClick={onOpen}>
          Add illustration
        </Button>
      </Box>

      <Card>
        {loading ? (
          <Box display="flex" justifyContent="center" p={Spacing.xxl}>
            <CircularProgress />
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Image</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items?.map((illustration: IllustrationModel) => (
                <IllustrationsPageIllustrationDetails
                  key={illustration?.id}
                  illustration={illustration}
                  onDelete={onDelete}
                />
              ))}
            </TableBody>

            <TableFooter>
              <TableRow>
                <TablePagination
                  page={page}
                  count={data?.adminListPaginatedIllustrations.total}
                  onPageChange={onChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={onChangeRowsPerPage}
                  rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                />
              </TableRow>
            </TableFooter>
          </Table>
        )}
      </Card>

      <IllustrationsPageCreateCont open={open} onClose={onClose} limit={limit} offset={offset} />
    </>
  );
};

export default IllustrationsPage;
